import { yupResolver } from '@hookform/resolvers/yup';
import { updateAccount } from 'api/user';
import { useAccount } from 'hooks/useAccount';
import { t } from 'i18next';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { IPasswordReset } from 'types/forms/passwordReset';
import { IUserForm } from 'types/forms/user';
import { changeProfilePasswordSchema } from 'validation/profileSchemas';
import type { HibpGateState } from './PasswordHibpSync';
import {
  getPasswordRulesPresentation,
  getPasswordRulesStatus
} from './passwordRulesPresentation';

export function useProfilePasswordForm() {
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hibpRef = useRef<HibpGateState>({ loading: false, pwned: false });
  const { loggedUserData } = useAccount();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid }
  } = useForm<IPasswordReset>({
    mode: 'onChange',
    defaultValues: {
      password: '',
      password_confirmation: ''
    },
    resolver: yupResolver(changeProfilePasswordSchema)
  });

  const passwordValue = watch('password');
  const passwordConfirmValue = watch('password_confirmation');

  const passwordsMatch =
    (passwordConfirmValue?.length ?? 0) > 0 &&
    passwordValue === passwordConfirmValue;

  const passwordRulesStatus = useMemo(
    () => getPasswordRulesStatus(passwordValue),
    [passwordValue]
  );

  const rulesPresentation = useMemo(
    () => getPasswordRulesPresentation(passwordRulesStatus),
    [passwordRulesStatus]
  );

  const translateFieldError = (message?: string) =>
    message ? t(message as string) : undefined;

  const onSubmit = handleSubmit(async (data) => {
    const { password } = data;
    const lower = password.toLowerCase();
    const emailPart = loggedUserData.email?.split('@')[0]?.toLowerCase() ?? '';

    if (
      (loggedUserData.email && lower.includes(loggedUserData.email.toLowerCase())) ||
      (emailPart && lower.includes(emailPart)) ||
      (loggedUserData.firstName &&
        lower.includes(loggedUserData.firstName.toLowerCase())) ||
      (loggedUserData.lastName && lower.includes(loggedUserData.lastName.toLowerCase()))
    ) {
      setIsErrorModalOpen(true);
      return;
    }

    if (hibpRef.current.loading) {
      toast.info(t('auth.passwordWaitHibpCheck'));
      return;
    }
    if (hibpRef.current.pwned) {
      setIsErrorModalOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        user: {
          password: data.password,
          password_confirmation: data.password_confirmation
        }
      } as unknown as IUserForm;
      await updateAccount(payload);
      toast.success(t('auth.passwordChanged'));
      reset({ password: '', password_confirmation: '' });
    } catch (e) {
      console.error(e);
      toast.error(t('anErrorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  });

  return {
    register,
    onSubmit,
    errors,
    isValid,
    passwordValue,
    passwordsMatch,
    isSubmitting,
    isErrorModalOpen,
    setIsErrorModalOpen,
    hibpRef,
    translateFieldError,
    ...rulesPresentation
  };
}
